import type { Program } from './guide.js';
import { reminderKey } from './reminders.js';

/**
 * Il palinsesto di un canale **da adesso in poi**, come lo apre una riga della guida sul telefono.
 *
 * ## La seconda dimensione, girata di novanta gradi
 *
 * Sul televisore la guida è una griglia: i canali in verticale, il tempo in orizzontale. Su uno
 * schermo alto e stretto quella forma non si rimpicciolisce — si **gira**: si tocca un canale e il
 * tempo si apre sotto di esso (`docs/04-SCHERMATE-MOBILE.md` §4).
 *
 * Il che cambia una cosa sostanziale: sulla griglia si vede anche **quel che è già passato**, perché
 * la finestra comincia prima di adesso. Aperto sotto una riga, un elenco che comincia da stamattina
 * costringerebbe a scorrere verso il basso per arrivare a quel che sta andando in onda — il contrario
 * di quel che si è chiesto toccando.
 *
 * ## Il contrassegno **è** il pulsante
 *
 * Non c'è un'etichetta che dice lo stato e un tasto accanto che agisce: sono la stessa cosa, e il
 * motivo è lo spazio — su 390 px una colonna di stato più una di azione lascerebbe al titolo meno
 * della metà della riga. Quindi ogni voce porta un solo contrassegno, che dice cos'è e cosa succede
 * toccandolo.
 */

/**
 * Cosa mostra — e cosa fa — il contrassegno di una voce.
 *
 * - `onAir`: sta andando in onda. Toccarlo guarda quel canale.
 * - `remind`: comincia più tardi, nessun promemoria. Toccarlo lo mette.
 * - `reminded`: comincia più tardi, promemoria già messo. Toccarlo lo toglie.
 *
 * Non esiste un contrassegno per quel che è finito: quelle voci non compaiono affatto.
 */
export type ScheduleTag = 'onAir' | 'remind' | 'reminded';

export interface ScheduleRow {
  readonly program: Program;
  readonly tag: ScheduleTag;
}

export interface ScheduleOptions {
  /**
   * Quante voci al massimo.
   *
   * Un canale può avere trenta programmi nelle prossime ventiquattr'ore, e aprirli tutti sotto una
   * riga vorrebbe dire che le righe sotto finiscono fuori dallo schermo: chi apre un canale per
   * sbaglio perde di vista tutto il resto dell'elenco. Il prototipo ne mostra pochi.
   */
  readonly limit?: number;
  /** Gli id dei programmi con un promemoria già messo. */
  readonly reminders?: ReadonlyArray<string>;
}

const DEFAULT_LIMIT = 6;

/**
 * Le voci da mostrare aprendo un canale.
 *
 * ## Si tiene quel che non è ancora finito, non quel che non è ancora cominciato
 *
 * La differenza è **il programma in onda adesso**, ed è l'unico che qualcuno stia effettivamente
 * guardando. Filtrando per `startMs >= adesso` sparirebbe: aprendo un canale, la prima voce sarebbe
 * quella delle 21:30 e di quel che c'è ora non ci sarebbe traccia — proprio l'informazione per cui
 * si è toccato.
 *
 * ## In ordine di orario, e non come arrivano
 *
 * L'ordine dei programmi in memoria lo decide chi ha riempito la cache, e può essere qualunque. Una
 * guida che elenca le 23:00 prima delle 21:00 non sembra un difetto di ordinamento: sembra un
 * palinsesto sbagliato, e si dà la colpa alla fonte.
 */
export function openedSchedule(
  programs: ReadonlyArray<Program>,
  channelId: string,
  nowMs: number,
  options: ScheduleOptions = {},
): ScheduleRow[] {
  const limit = options.limit ?? DEFAULT_LIMIT;
  const reminders = new Set(options.reminders ?? []);

  /*
    `.filter()` restituisce già un array nuovo, quindi `.sort()` qui non tocca l'elenco ricevuto.

    Importa, e non è una finezza: questi programmi sono **la cache condivisa** con la guida del
    televisore e con le fasce della home. Un ordinamento in posto si manifesterebbe altrove — una
    fascia che cambia ordine — e nessuno lo collegherebbe all'aver toccato un canale.

    C'era un `.slice()` in mezzo, messo per quella ragione. Era inutile, e la prova che credeva di
    difenderlo passava anche togliendolo: una prova che non può fallire è peggio di nessuna prova,
    perché fa credere coperto qualcosa che non lo è.
  */
  return programs
    .filter((program) => program.channelId === channelId && program.endMs > nowMs)
    .sort((a, b) => a.startMs - b.startMs)
    .slice(0, Math.max(0, limit))
    .map((program) => ({ program, tag: tagFor(program, nowMs, reminders) }));
}

/**
 * Quale contrassegno porta una voce.
 *
 * `onAir` vince su tutto: un promemoria su un programma già cominciato non ha più niente da
 * ricordare, e mostrarlo come `✓ RICORDA` direbbe a chi guarda che c'è ancora qualcosa da aspettare
 * mentre il programma sta andando.
 */
function tagFor(program: Program, nowMs: number, reminders: ReadonlySet<string>): ScheduleTag {
  if (program.startMs <= nowMs && nowMs < program.endMs) return 'onAir';
  /*
    L'identità di un promemoria è `reminderKey`, **non** `program.id`.

    Qui c'era `reminders.has(program.id)`, e non combaciava mai: `program.id` vale `canale-slot`
    (lo compone `ProgramStoreBuilder`), mentre l'archivio e la schermata del televisore usano
    `canale@istante`. Nessuna delle due parti sbagliava da sola — ognuna era provata con i propri
    dati finti, ed entrambe le suite erano verdi — ma messe insieme la stellina «promemoria
    impostato» sarebbe rimasta spenta per sempre, su un tasto che rispondeva.

    Lo `slot` per giunta è una posizione nel magazzino: cambia quando cambia il palinsesto, quindi
    non è nemmeno un identificativo stabile fra due letture.
  */
  return reminders.has(reminderKey(program.channelId, program.startMs)) ? 'reminded' : 'remind';
}
